import React, { useState, useEffect, useMemo } from "react";
import Paper from "@mui/material/Paper";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Typography,
  TextField,
  Button,
  Stack,
  Autocomplete,
  Checkbox,
  Chip,
  InputAdornment,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { BarChart } from "@mui/x-charts/BarChart";
import { LinePlot, MarkPlot } from "@mui/x-charts/LineChart";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {getProjects} from "../../api/projectAPI";
import { useTranslation } from "react-i18next";
import ConvertOIDValueToString from "../../utils/objectConverter";
// import { getCurrentTenant } from "../../config/tenantConfig";
import HighlightedCard from "../../components/HighlightedCard";
import TopProductCard from "../../components/TopProductCard";
import { getNode } from "../../api/nodeAPI";
import {
  getSaleList,
  getSalesGroupByProduct,
  getSalesGroupByDay,
  getSalesGroupByNid,
} from "../../api/topicDataAPI";
import { getRegion } from "../../api/regionAPI";
import { useObjectData } from "../../hooks/useLocalStorageData";
import { getRegionLevel } from "../regions/RegionGrid";
import * as XLSX from "xlsx-js-style";
import TimePeriod from "../../config/timePeriod";

// const { apiBaseUrl: API_BASE_URL } = getCurrentTenant(); // import { TableBar } from "@mui/icons-material"; // unused

function WidgetList({
  widget,
  objectData,
  startEpoch,
  endEpoch,
  selectedTimePeriod,
  selectedMachine,
  selectedRegion,
  selectedSubRegion,
  allRegions,
  getAllChildRegions,
  machines,
  onExportToExcel,
  onExportTableToExcel,
  onLoadingChange,
  filterStartDate,
  filterEndDate,
  filterTimePeriod,
  filterSelectedMachine,
  filterMachines,
  filterSelectedRegion,
  filterSelectedSubRegion,
  groupMode = "default",
  selectedPaymentTypes = [],
}) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, _setLoading] = useState(true);
  const setLoading = React.useCallback((val) => {
    _setLoading(val);
    if (onLoadingChange) onLoadingChange(val);
  }, [onLoadingChange]);
  const [error, setError] = useState(null);
  const [totalSalesAmount, setTotalSalesAmount] = useState(0);
  const [totalSalesCount, setTotalSalesCount] = useState(0);
  const [cashSalesAmount, setCashSalesAmount] = useState(0);
  const [cashSalesCount, setCashSalesCount] = useState(0);
  const [mobileSalesAmount, setMobileSalesAmount] = useState(0);
  const [mobileSalesCount, setMobileSalesCount] = useState(0);
  const [cardSalesAmount, setCardSalesAmount] = useState(0);
  const [cardSalesCount, setCardSalesCount] = useState(0);
  const [topMachineName, setTopMachineName] = useState("");
  const [topMachineAmount, setTopMachineAmount] = useState(0);
  const [topCountMachineName, setTopCountMachineName] = useState("");
  const [topProductName, setTopProductName] = useState("");
  const [topProductCount, setTopProductCount] = useState(0);
  const [salesChartData, setSalesChartData] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  const [groupByProductData, setGroupByProductData] = useState([]);
  const [groupByDayData, setGroupByDayData] = useState([]);
  const [groupByNidData, setGroupByNidData] = useState([]);
  const [chartsHidden, setChartsHidden] = useState(false);

  useEffect(() => {
    setPaginationModel((pm) => ({ ...pm, page: 0 }));
  }, [groupMode]);

  // Sadece seçili grup moduna göre ilgili endpoint'i çağır
  useEffect(() => {
    if (groupMode === "default") return;

    let nodeList = [];
    if (selectedMachine && selectedMachine.length > 0 && machines && machines.length > 0) {
      nodeList = machines
        .filter((m) => selectedMachine.includes(m.name))
        .map((m) => m.idNode)
        .filter(Boolean);
    } else if (machines && machines.length > 0) {
      nodeList = machines.map((m) => m.idNode).filter(Boolean);
    }
    const timePeriod =
      selectedTimePeriod !== null
        ? selectedTimePeriod
        : startEpoch !== null || endEpoch !== null
        ? TimePeriod.Unfiltered
        : TimePeriod.Today;
    const saleOptions = {
      ...(startEpoch !== null && { start: startEpoch }),
      ...(endEpoch !== null && { end: endEpoch }),
      count: 0,
      timePeriod,
      val: Array.isArray(selectedPaymentTypes) ? selectedPaymentTypes : [],
    };
    const extractGroupData = (resp) => {
      if (Array.isArray(resp)) return resp;
      if (resp && Array.isArray(resp.data)) return resp.data;
      if (resp && Array.isArray(resp.rows)) return resp.rows;
      return [];
    };

    let cancelled = false;
    (async () => {
      try {
        if (groupMode === "product") {
          const resp = await getSalesGroupByProduct(nodeList, saleOptions);
          if (!cancelled) setGroupByProductData(extractGroupData(resp));
        } else if (groupMode === "day") {
          const resp = await getSalesGroupByDay(nodeList, saleOptions);
          if (!cancelled) setGroupByDayData(extractGroupData(resp));
        } else if (groupMode === "nid") {
          const resp = await getSalesGroupByNid(nodeList, saleOptions);
          if (!cancelled) setGroupByNidData(extractGroupData(resp));
        }
      } catch (err) {
        console.error("[Sales] Group endpoint error:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    groupMode,
    startEpoch,
    endEpoch,
    selectedTimePeriod,
    machines,
    selectedMachine,
    selectedPaymentTypes,
  ]);

  const ROW_HEIGHT = 44;
  const HEADER_HEIGHT = 48;
  const FOOTER_HEIGHT = 52;

  // Excel Export Fonksiyonu (GetAllEvetData API - event 1011)
  const handleExportToExcel = React.useCallback(async () => {
    // Grup modlarında canlı filtre değerleriyle yeniden API'ye istek at, dönen veriyi dışa aktar
    if (groupMode !== "default") {
      setLoading(true);
      try {
        const now = new Date();
        const currentTimeInSeconds = Math.floor(now.getTime() / 1000);

        // Tarih aralığını canlı filtre state'inden hesapla (default mod ile aynı mantık)
        let exportStart = null;
        let exportEnd = null;
        if (filterStartDate) {
          exportStart = Math.floor(new Date(filterStartDate).getTime() / 1000);
        }
        if (filterEndDate) {
          exportEnd = Math.floor(new Date(filterEndDate).getTime() / 1000);
        }
        if (exportStart === null || exportEnd === null) {
          const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const todayStartEpoch = Math.floor(todayStart.getTime() / 1000);
          if (filterTimePeriod === TimePeriod.Today) {
            exportStart = todayStartEpoch;
            exportEnd = currentTimeInSeconds;
          } else if (filterTimePeriod === TimePeriod.Yesterday) {
            exportStart = todayStartEpoch - 86400;
            exportEnd = todayStartEpoch;
          } else if (filterTimePeriod === TimePeriod.ThisWeek) {
            const dayOfWeek = now.getDay();
            const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            exportStart = todayStartEpoch - diffToMonday * 86400;
            exportEnd = currentTimeInSeconds;
          } else if (filterTimePeriod === TimePeriod.LastWeek) {
            const dayOfWeek = now.getDay();
            const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            const thisWeekMonday = todayStartEpoch - diffToMonday * 86400;
            exportStart = thisWeekMonday - 7 * 86400;
            exportEnd = thisWeekMonday;
          } else if (filterTimePeriod === TimePeriod.LastMonth) {
            const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
            const lastDay = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
            exportStart = Math.floor(firstDay.getTime() / 1000);
            exportEnd = Math.floor(lastDay.getTime() / 1000);
          } else if (filterTimePeriod === TimePeriod.ThisMonth) {
            const firstDay = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
            exportStart = Math.floor(firstDay.getTime() / 1000);
            exportEnd = currentTimeInSeconds;
          } else {
            exportStart = currentTimeInSeconds - 259200;
            exportEnd = currentTimeInSeconds;
          }
        }

        // nodeList: canlı filtre state'inden
        let nodeListForExport = [];
        if (filterSelectedMachine.length > 0 && filterMachines && filterMachines.length > 0) {
          nodeListForExport = filterMachines
            .filter((m) => filterSelectedMachine.includes(m.name))
            .map((m) => m.idNode)
            .filter(Boolean);
        } else if ((filterSelectedRegion.length > 0 || filterSelectedSubRegion.length > 0) && filterMachines && filterMachines.length > 0) {
          nodeListForExport = filterMachines.map((m) => m.idNode).filter(Boolean);
        }

        const opts = {
          event: [1011],
          start: exportStart,
          end: exportEnd,
          timePeriod: TimePeriod.Unfiltered,
          count: 0,
          val: Array.isArray(selectedPaymentTypes) ? selectedPaymentTypes : [],
        };

        const extract = (resp) => {
          if (Array.isArray(resp)) return resp;
          if (resp && Array.isArray(resp.data)) return resp.data;
          if (resp && Array.isArray(resp.rows)) return resp.rows;
          return [];
        };

        let freshData = [];
        if (groupMode === "product") {
          freshData = extract(await getSalesGroupByProduct(nodeListForExport, opts));
        } else if (groupMode === "day") {
          freshData = extract(await getSalesGroupByDay(nodeListForExport, opts));
        } else if (groupMode === "nid") {
          freshData = extract(await getSalesGroupByNid(nodeListForExport, opts));
        }

        const wb = XLSX.utils.book_new();
        let sheetName = "Report";
        let header = [];
        let body = [];
        const fmtAmount = (v) => {
          const n = Number(v);
          return Number.isNaN(n) ? v : n / 100;
        };
        if (groupMode === "product") {
          sheetName = t("vendingSales.viewByProduct", "Ürüne Göre");
          header = [
            t("vendingSales.product", "Ürün"),
            t("vendingSales.salesCount", "Satış Adedi"),
            t("vendingSales.totalAmount", "Toplam Tutar") + " (₺)",
          ];
          body = [...freshData]
            .sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0))
            .map((r) => [r.productId ?? "-", r.salesCount ?? 0, fmtAmount(r.totalAmount)]);
        } else if (groupMode === "day") {
          sheetName = t("vendingSales.viewByDay", "Güne Göre");
          header = [
            t("widgets.date", "Tarih"),
            t("vendingSales.salesCount", "Satış Adedi"),
            t("vendingSales.totalAmount", "Toplam Tutar") + " (₺)",
          ];
          body = [...freshData]
            .sort((a, b) => String(a.date).localeCompare(String(b.date)))
            .map((r) => [r.date ?? "-", r.salesCount ?? 0, fmtAmount(r.totalAmount)]);
        } else if (groupMode === "nid") {
          sheetName = t("vendingSales.viewByMachine", "Otomata Göre");
          header = [
            t("vendingSales.machine", "Otomat"),
            t("vendingSales.salesCount", "Satış Adedi"),
            t("vendingSales.totalAmount", "Toplam Tutar") + " (₺)",
          ];
          body = [...freshData]
            .sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0))
            .map((r) => [
              r.NodeName ?? `NID ${r.NID}`,
              r.salesCount ?? 0,
              fmtAmount(r.totalAmount),
            ]);
        }

        if (body.length === 0) {
          alert(t("vendingSales.noData", "Tabloda veri yok."));
          return;
        }

        const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
        const range = XLSX.utils.decode_range(ws["!ref"]);
        for (let c = range.s.c; c <= range.e.c; c++) {
          const cellAddr = XLSX.utils.encode_cell({ r: 0, c });
          if (ws[cellAddr]) {
            ws[cellAddr].s = {
              font: { bold: true },
              fill: { fgColor: { rgb: "E0E0E0" } },
              alignment: { horizontal: "center" },
            };
          }
        }
        ws["!cols"] = header.map(() => ({ wch: 22 }));
        XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
        const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14);
        XLSX.writeFile(wb, `sales_${groupMode}_${ts}.xlsx`);
      } catch (err) {
        console.error("[ExportGroup] error:", err);
        alert(t("vendingSales.exportError", "Dışa aktarma hatası"));
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      const now = new Date();
      const currentTimeInSeconds = Math.floor(now.getTime() / 1000);

      // Başlangıç ve bitiş epoch'larını hesapla
      let exportStart = null;
      let exportEnd = null;

      // Önce tarih input'larına bak (kullanıcı datetime-local seçmişse)
      if (filterStartDate) {
        exportStart = Math.floor(new Date(filterStartDate).getTime() / 1000);
      }
      if (filterEndDate) {
        exportEnd = Math.floor(new Date(filterEndDate).getTime() / 1000);
      }

      // Tarih input'ları boşsa hızlı filtre butonuna göre hesapla
      if (exportStart === null || exportEnd === null) {
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayStartEpoch = Math.floor(todayStart.getTime() / 1000);

        if (filterTimePeriod === TimePeriod.Today) {
          exportStart = todayStartEpoch;
          exportEnd = currentTimeInSeconds;
        } else if (filterTimePeriod === TimePeriod.Yesterday) {
          exportStart = todayStartEpoch - 86400;
          exportEnd = todayStartEpoch;
        } else if (filterTimePeriod === TimePeriod.ThisWeek) {
          const dayOfWeek = now.getDay();
          const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          exportStart = todayStartEpoch - (diffToMonday * 86400);
          exportEnd = currentTimeInSeconds;
        } else if (filterTimePeriod === TimePeriod.LastWeek) {
          const dayOfWeek = now.getDay();
          const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          const thisWeekMonday = todayStartEpoch - (diffToMonday * 86400);
          exportStart = thisWeekMonday - (7 * 86400);
          exportEnd = thisWeekMonday;
        } else if (filterTimePeriod === TimePeriod.LastMonth) {
          const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
          const lastDay = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
          exportStart = Math.floor(firstDay.getTime() / 1000);
          exportEnd = Math.floor(lastDay.getTime() / 1000);
        } else if (filterTimePeriod === TimePeriod.ThisMonth) {
          const firstDay = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
          exportStart = Math.floor(firstDay.getTime() / 1000);
          exportEnd = currentTimeInSeconds;
        } else {
          exportStart = currentTimeInSeconds - 259200;
          exportEnd = currentTimeInSeconds;
        }
      }

      // nodeList: seçili otomatlar varsa onları, bölge seçiliyse bölgedeki otomatları gönder
      let nodeListForExport = [];
      if (filterSelectedMachine.length > 0 && filterMachines && filterMachines.length > 0) {
        // Spesifik otomat seçilmişse sadece onların idNode'larını gönder
        nodeListForExport = filterMachines
          .filter((m) => filterSelectedMachine.includes(m.name))
          .map((m) => m.idNode)
          .filter(Boolean);
      } else if ((filterSelectedRegion.length > 0 || filterSelectedSubRegion.length > 0) && filterMachines && filterMachines.length > 0) {
        // Otomat seçilmemiş ama bölge/alt bölge seçilmişse o bölgedeki otomatları gönder
        nodeListForExport = filterMachines.map((m) => m.idNode).filter(Boolean);
      }
      // Hiçbir şey seçilmediyse boş array → sunucu tüm verileri döner

      console.log("[ExportToExcel] Debug:", {
        filterSelectedMachine,
        filterSelectedRegion,
        filterSelectedSubRegion,
        filterMachinesCount: filterMachines?.length,
        filterMachinesSample: filterMachines?.[0],
        nodeList: nodeListForExport,
        start: exportStart,
        end: exportEnd,
      });

      const response = await getSaleList(nodeListForExport, {
        event: [1011],
        start: exportStart,
        end: exportEnd,
        timePeriod: TimePeriod.Unfiltered,
        count: 0,
      });

      let records = [];
      if (Array.isArray(response)) {
        records = response;
      } else if (response && Array.isArray(response.data)) {
        records = response.data;
      } else if (response && Array.isArray(response.rows)) {
        records = response.rows;
      }

      // Filtreleme uygula
      let filteredMachineNames = selectedMachine;
      if (selectedMachine.length === 0 && machines && machines.length > 0) {
        filteredMachineNames = machines.map((m) => m.name);
      }

      const filteredRecords =
        filteredMachineNames.length > 0
          ? records.filter((rec) =>
              filteredMachineNames.includes(rec?.NodeName)
            )
          : records;

      // Toplam istatistikleri hesapla
      // Yeni API'de ödeme tipi "val" field'ında: 307=Nakit, 309=Mobil, 314=Kart
      // Diğer field'lar doğrudan key: 1002=Ürün Kodu, 1003=Ürün Adı, 1004=Tutar (kuruş)
      let totalAmount = 0;
      let totalCount = 0;
      let cashAmount = 0;
      let cashCount = 0;
      let mobileAmount = 0;
      let mobileCount = 0;
      let cardAmount = 0;
      let cardCount = 0;

      filteredRecords.forEach((rec) => {
        const paymentType = rec.val;
        const amount = rec["1004"];
        let numAmount = 0;
        if (amount !== null && amount !== undefined) {
          numAmount = Number(amount);
          if (Number.isNaN(numAmount)) numAmount = 0;
        }

        if (paymentType === 307) {
          cashCount++;
          cashAmount += numAmount;
          totalCount++;
          totalAmount += numAmount;
        } else if (paymentType === 309) {
          mobileCount++;
          mobileAmount += numAmount;
          totalCount++;
          totalAmount += numAmount;
        } else if (paymentType === 314) {
          cardCount++;
          cardAmount += numAmount;
          totalCount++;
          totalAmount += numAmount;
        }
      });

      // Tarih aralığı metnini hesaplanan exportStart/exportEnd'den oluştur
      const rangeStartDate = new Date(exportStart * 1000);
      const rangeEndDate = new Date(exportEnd * 1000);
      const dateRangeText = `${rangeStartDate.toLocaleString("tr", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })} - ${rangeEndDate.toLocaleString("tr", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })}`;

      // Workbook oluştur
      const wb = XLSX.utils.book_new();

      // Helper: epoch'u tarih string'e çevir
      const epochToDateStr = (rawEpoch) => {
        if (rawEpoch === undefined || rawEpoch === null || rawEpoch === "") return "-";
        let epochNum = Number(rawEpoch);
        if (Number.isNaN(epochNum)) return "-";
        if (epochNum < 1e12) epochNum *= 1000;
        const d = new Date(epochNum);
        if (isNaN(d.getTime())) return "-";
        return d.toLocaleString("tr", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      };

      // Helper: ödeme tipi adı
      const getPaymentTypeName = (val) => {
        if (val === 307) return t("vendingSales.cash", "Nakit");
        if (val === 309) return t("vendingSales.mobile", "Mobil");
        if (val === 314) return t("vendingSales.card", "Kart");
        return "-";
      };

      // Helper: bold satır oluştur ve uygula
      const applyBoldToRow = (ws, rowIndex, colCount) => {
        for (let c = 0; c < colCount; c++) {
          const addr = XLSX.utils.encode_cell({ r: rowIndex, c });
          if (ws[addr]) {
            ws[addr].s = { ...ws[addr].s, font: { bold: true } };
          }
        }
      };

      // Helper: tüm hücreleri ortala
      const applyCenterToSheet = (ws) => {
        const range = XLSX.utils.decode_range(ws['!ref'] || "A1");
        for (let R = range.s.r; R <= range.e.r; ++R) {
          for (let C = range.s.c; C <= range.e.c; ++C) {
            const addr = XLSX.utils.encode_cell({ r: R, c: C });
            if (ws[addr]) {
              ws[addr].s = {
                ...ws[addr].s,
                alignment: { ...(ws[addr].s?.alignment || {}), horizontal: "center", vertical: "center" },
              };
            }
          }
        }
      };

      // =============================================
      // SAYFA 1: ÖZET
      // =============================================
      const summaryData = [
        [t("vendingSales.salesReport", "SATIŞ RAPORU")],
        [t("vendingSales.dateRange", "Tarih Aralığı"), dateRangeText],
        [t("vendingSales.totalRecords", "Toplam Kayıt"), filteredRecords.length],
        [],
        [t("vendingSales.category", "Kategori"), t("vendingSales.quantity", "Adet"), t("vendingSales.amountTL", "Tutar (TL)")],
        [t("vendingSales.totalSales", "Toplam Satış"), totalCount, Number((totalAmount / 100).toFixed(2))],
        [t("vendingSales.cardSales", "Kart Satış"), cardCount, Number((cardAmount / 100).toFixed(2))],
        [t("vendingSales.cashSales", "Nakit Satış"), cashCount, Number((cashAmount / 100).toFixed(2))],
        [t("vendingSales.mobileSales", "Mobil Satış"), mobileCount, Number((mobileAmount / 100).toFixed(2))],
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      ws1["!cols"] = [{ wch: 25 }, { wch: 20 }, { wch: 20 }];
      applyCenterToSheet(ws1);
      applyBoldToRow(ws1, 0, 3); // Başlık
      applyBoldToRow(ws1, 4, 3); // Tablo header
      XLSX.utils.book_append_sheet(wb, ws1, t("vendingSales.summary", "Özet"));

      // =============================================
      // SAYFA 2: OTOMAT BAZINDA
      // =============================================
      const machineData = {};
      filteredRecords.forEach((rec) => {
        const machineName = rec?.NodeName || t("vendingSales.unknown", "Bilinmeyen");
        const paymentType = rec.val;
        const amount = rec["1004"];

        if (paymentType === 307 || paymentType === 309 || paymentType === 314) {
          if (!machineData[machineName]) {
            machineData[machineName] = {
              totalCount: 0, totalAmount: 0,
              cashCount: 0, cashAmount: 0,
              mobileCount: 0, mobileAmount: 0,
              cardCount: 0, cardAmount: 0,
            };
          }

          let numAmount = 0;
          if (amount !== null && amount !== undefined) {
            numAmount = Number(amount);
            if (Number.isNaN(numAmount)) numAmount = 0;
          }

          machineData[machineName].totalCount++;
          machineData[machineName].totalAmount += numAmount;

          if (paymentType === 307) {
            machineData[machineName].cashCount++;
            machineData[machineName].cashAmount += numAmount;
          } else if (paymentType === 309) {
            machineData[machineName].mobileCount++;
            machineData[machineName].mobileAmount += numAmount;
          } else if (paymentType === 314) {
            machineData[machineName].cardCount++;
            machineData[machineName].cardAmount += numAmount;
          }
        }
      });

      const machineHeader = [
        t("vendingSales.machineName", "Otomat Adı"),
        t("vendingSales.totalQuantity", "Toplam Adet"),
        t("vendingSales.totalAmountTL", "Toplam Tutar (TL)"),
        t("vendingSales.cardQuantity", "Kart Adet"),
        t("vendingSales.cardAmountTL", "Kart Tutar (TL)"),
        t("vendingSales.cashQuantity", "Nakit Adet"),
        t("vendingSales.cashAmountTL", "Nakit Tutar (TL)"),
        t("vendingSales.mobileQuantity", "Mobil Adet"),
        t("vendingSales.mobileAmountTL", "Mobil Tutar (TL)"),
      ];

      const machineRows = [
        [t("vendingSales.machineBasedTotalData", "OTOMAT BAZINDA TOPLAM VERİLER")],
        [t("vendingSales.dateRange", "Tarih Aralığı"), dateRangeText],
        [],
        machineHeader,
      ];

      // Toplam satırı için sayaçlar
      let grandTotalCount = 0, grandTotalAmount = 0;
      let grandCardCount = 0, grandCardAmount = 0;
      let grandCashCount = 0, grandCashAmount = 0;
      let grandMobileCount = 0, grandMobileAmount = 0;

      Object.entries(machineData)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([machineName, stats]) => {
          machineRows.push([
            machineName,
            stats.totalCount,
            Number((stats.totalAmount / 100).toFixed(2)),
            stats.cardCount,
            Number((stats.cardAmount / 100).toFixed(2)),
            stats.cashCount,
            Number((stats.cashAmount / 100).toFixed(2)),
            stats.mobileCount,
            Number((stats.mobileAmount / 100).toFixed(2)),
          ]);
          grandTotalCount += stats.totalCount;
          grandTotalAmount += stats.totalAmount;
          grandCardCount += stats.cardCount;
          grandCardAmount += stats.cardAmount;
          grandCashCount += stats.cashCount;
          grandCashAmount += stats.cashAmount;
          grandMobileCount += stats.mobileCount;
          grandMobileAmount += stats.mobileAmount;
        });

      // Toplam satırı
      const grandTotalRowIndex = machineRows.length;
      machineRows.push([
        t("vendingSales.grandTotal", "TOPLAM"),
        grandTotalCount,
        Number((grandTotalAmount / 100).toFixed(2)),
        grandCardCount,
        Number((grandCardAmount / 100).toFixed(2)),
        grandCashCount,
        Number((grandCashAmount / 100).toFixed(2)),
        grandMobileCount,
        Number((grandMobileAmount / 100).toFixed(2)),
      ]);

      const ws2 = XLSX.utils.aoa_to_sheet(machineRows);
      ws2["!cols"] = [
        { wch: 25 }, { wch: 15 }, { wch: 20 },
        { wch: 12 }, { wch: 18 }, { wch: 12 },
        { wch: 18 }, { wch: 12 }, { wch: 18 },
      ];
      applyCenterToSheet(ws2);
      applyBoldToRow(ws2, 0, 9); // Başlık
      applyBoldToRow(ws2, 3, 9); // Tablo header
      applyBoldToRow(ws2, grandTotalRowIndex, 9); // Toplam satırı
      XLSX.utils.book_append_sheet(wb, ws2, t("vendingSales.byMachine", "Otomat Bazında"));

      // =============================================
      // SAYFA 3: ÜRÜN BAZINDA
      // =============================================
      const productStats = {};
      filteredRecords.forEach((rec) => {
        const paymentType = rec.val;
        if (paymentType === 307 || paymentType === 309 || paymentType === 314) {
          const productCode = rec["1002"];
          const productName = rec["1003"];
          const amount = rec["1004"];
          const key = productCode !== null && productCode !== undefined ? String(productCode) : "-";

          if (!productStats[key]) {
            productStats[key] = { name: productName || "-", count: 0, amount: 0 };
          }
          if (productStats[key].name === "-" && productName) {
            productStats[key].name = productName;
          }
          let numAmount = 0;
          if (amount !== null && amount !== undefined) {
            numAmount = Number(amount);
            if (Number.isNaN(numAmount)) numAmount = 0;
          }
          productStats[key].count++;
          productStats[key].amount += numAmount;
        }
      });

      const productHeader = [
        t("vendingSales.productCode", "Ürün Kodu"),
        t("vendingSales.productName", "Ürün Adı"),
        t("vendingSales.totalQuantity", "Toplam Adet"),
        t("vendingSales.totalAmountTL", "Toplam Tutar (TL)"),
      ];

      const productRows = [
        [t("vendingSales.productBasedStatistics", "ÜRÜN BAZINDA SATIŞ İSTATİSTİKLERİ")],
        [t("vendingSales.dateRange", "Tarih Aralığı"), dateRangeText],
        [],
        productHeader,
      ];

      let productGrandCount = 0, productGrandAmount = 0;

      Object.entries(productStats)
        .sort(([, a], [, b]) => b.count - a.count)
        .forEach(([code, stats]) => {
          productRows.push([
            code,
            stats.name,
            stats.count,
            Number((stats.amount / 100).toFixed(2)),
          ]);
          productGrandCount += stats.count;
          productGrandAmount += stats.amount;
        });

      // Toplam satırı
      const productTotalRowIndex = productRows.length;
      productRows.push([
        "",
        t("vendingSales.grandTotal", "TOPLAM"),
        productGrandCount,
        Number((productGrandAmount / 100).toFixed(2)),
      ]);

      const ws3 = XLSX.utils.aoa_to_sheet(productRows);
      ws3["!cols"] = [{ wch: 15 }, { wch: 30 }, { wch: 15 }, { wch: 20 }];
      applyCenterToSheet(ws3);
      applyBoldToRow(ws3, 0, 4); // Başlık
      applyBoldToRow(ws3, 3, 4); // Tablo header
      applyBoldToRow(ws3, productTotalRowIndex, 4); // Toplam satırı
      XLSX.utils.book_append_sheet(wb, ws3, t("vendingSales.byProduct", "Ürün Bazında"));

      // =============================================
      // SAYFA 4: DETAYLI KAYITLAR
      // =============================================
      const detailHeader = [
        t("vendingSales.machineName", "Otomat Adı"),
        t("vendingSales.date", "Tarih"),
        t("vendingSales.paymentType", "Ödeme Tipi"),
        t("vendingSales.amountTL", "Tutar (TL)"),
        t("vendingSales.productCode", "Ürün Kodu"),
        t("vendingSales.productName", "Ürün Adı"),
        t("vendingSales.dsn", "Cihaz Seri No"),
        t("vendingSales.transactionStatus", "İşlem Durumu"),
        t("vendingSales.bankRefNo", "Banka Ref No"),
      ];

      const detailRows = [
        [t("vendingSales.detailedSalesRecords", "DETAYLI SATIŞ KAYITLARI")],
        [t("vendingSales.dateRange", "Tarih Aralığı"), dateRangeText],
        [],
        detailHeader,
      ];

      const allSalesRecords = filteredRecords
        .filter((rec) => {
          const pt = rec.val;
          return pt === 307 || pt === 309 || pt === 314;
        })
        .sort((a, b) => {
          const nameA = a?.NodeName || "";
          const nameB = b?.NodeName || "";
          const nameCompare = nameA.localeCompare(nameB);
          if (nameCompare !== 0) return nameCompare;
          const epochA = Number(a?.epoch) || 0;
          const epochB = Number(b?.epoch) || 0;
          return epochB - epochA;
        });

      allSalesRecords.forEach((rec) => {
        const amount = rec["1004"];
        let numAmount = 0;
        if (amount !== null && amount !== undefined) {
          numAmount = Number(amount);
          if (Number.isNaN(numAmount)) numAmount = 0;
        }

        const bankRef = rec["223"];
        detailRows.push([
          rec?.NodeName || "-",
          epochToDateStr(rec?.epoch),
          getPaymentTypeName(rec.val),
          Number((numAmount / 100).toFixed(2)),
          rec["1002"] ?? "-",
          rec["1003"] ?? "-",
          rec?.DSN ?? "-",
          rec["206"] ?? "-",
          bankRef === null || bankRef === undefined || String(bankRef).trim() === ""
            ? "-"
            : bankRef,
        ]);
      });

      const ws4 = XLSX.utils.aoa_to_sheet(detailRows);
      ws4["!cols"] = [
        { wch: 25 }, { wch: 22 }, { wch: 14 }, { wch: 14 },
        { wch: 12 }, { wch: 28 }, { wch: 20 }, { wch: 22 }, { wch: 20 },
      ];
      applyCenterToSheet(ws4);
      applyBoldToRow(ws4, 0, 9); // Başlık
      applyBoldToRow(ws4, 3, 9); // Tablo header
      XLSX.utils.book_append_sheet(wb, ws4, t("vendingSales.details", "Detaylı Kayıtlar"));

      // Dosyayı indir
      const fileName = `${t("vendingSales.salesReportFileName", "Satis_Raporu")}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Excel export error:", error);
      const isTimeout = error?.code === "ECONNABORTED" || error?.message?.includes("timeout");
      const isNetwork = !error?.response && !error?.returnCode;
      if (isTimeout || isNetwork) {
        alert(t("vendingSales.excelExportTimeout", "Veri çok büyük veya sunucuya ulaşılamıyor. Lütfen daha kısa bir tarih aralığı seçin veya bölge/otomat filtresi kullanın."));
      } else {
        alert(t("vendingSales.excelExportError", "Excel dosyası oluşturulurken bir hata oluştu. Lütfen daha kısa bir tarih aralığı seçin veya bölge/otomat filtresi kullanın."));
      }
    } finally {
      setLoading(false);
    }
  }, [filterStartDate, filterEndDate, filterTimePeriod, filterSelectedMachine, filterMachines, filterSelectedRegion, filterSelectedSubRegion, groupMode, groupByProductData, groupByDayData, groupByNidData, t]);

  // Export fonksiyonunu parent'a gönder
  React.useEffect(() => {
    if (onExportToExcel) {
      onExportToExcel(handleExportToExcel);
    }
  }, [handleExportToExcel, onExportToExcel]);

  // Tablodaki veriyi Excel'e çıkaran fonksiyon
  const handleExportTableToExcel = React.useCallback(() => {
    try {
      if (!rows || rows.length === 0) {
        alert(t("vendingSales.noData", "Tabloda veri yok."));
        return;
      }

      const wb = XLSX.utils.book_new();

      // Kolon başlıklarını oluştur (columns henüz burada erişilemeyeceği için widget.targetObjectList'ten)
      const lang = (
        localStorage.getItem("i18nextLng") ||
        i18n.language ||
        "en"
      ).split("-")[0].toLowerCase();

      const headerRow = [t("widgets.date", "Tarih")];
      const fieldOrder = ["date"];

      if (Array.isArray(widget.targetObjectList)) {
        widget.targetObjectList
          .filter((c) => c !== "NID")
          .forEach((code) => {
            const field = code.toString();
            let headerName = field;
            const numericCode = parseInt(field, 10);
            if (!Number.isNaN(numericCode)) {
              const objInfo = objectData.find((o) => o.code === numericCode);
              if (objInfo) {
                headerName =
                  lang === "tr"
                    ? objInfo.name1 ?? objInfo.name
                    : lang === "de"
                    ? objInfo.name3 ?? objInfo.name
                    : lang === "fr"
                    ? objInfo.name4 ?? objInfo.name
                    : lang === "es"
                    ? objInfo.name5 ?? objInfo.name
                    : objInfo.name ?? field;
              }
            }
            headerRow.push(headerName);
            fieldOrder.push(field);
          });
      }

      const allData = [headerRow];

      rows.forEach((row) => {
        const rowData = fieldOrder.map((field) => {
          const val = row[field];
          return val !== null && val !== undefined ? val : "-";
        });
        allData.push(rowData);
      });

      const worksheet = XLSX.utils.aoa_to_sheet(allData);

      // Sütun genişliklerini ayarla
      worksheet["!cols"] = fieldOrder.map(() => ({ wch: 20 }));

      XLSX.utils.book_append_sheet(wb, worksheet, t("vendingSales.salesReport", "Satış Raporu"));

      const fileName = `${t("vendingSales.tableSalesReportFileName", "Tablo_Satis_Raporu")}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Table Excel export error:", error);
      alert(t("vendingSales.excelExportError", "Excel dosyası oluşturulurken bir hata oluştu."));
    }
  }, [rows, widget, objectData, i18n.language]);

  // Tablo export fonksiyonunu parent'a gönder
  React.useEffect(() => {
    if (onExportTableToExcel) {
      onExportTableToExcel(handleExportTableToExcel);
    }
  }, [handleExportTableToExcel, onExportTableToExcel]);

  const columns = useMemo(() => {
    if (!Array.isArray(widget.targetObjectList)) return [];
    const dateColumn = {
      field: "date",
      headerName: t("widgets.date", "Date"),
      flex: 1,
      minWidth: 130,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            width: "100%",
            textAlign: "center",
            fontSize: "0.7rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.value ?? "-"}
        </Typography>
      ),
    };

    const lang = (
      localStorage.getItem("i18nextLng") ||
      i18n.language ||
      "en"
    ).split("-")[0].toLowerCase(); 
    const dynamicColumns = widget.targetObjectList
      .filter((c) => c !== "NID" && c !== "1010" && c !== 1010 && c !== "223" && c !== 223)
      .map((code) => {
        const field = code.toString();
        let headerName = field;
        const numericCode = parseInt(field, 10);
        if (!Number.isNaN(numericCode)) {
          const objInfo = objectData.find((o) => o.code === numericCode);
          if (objInfo) {
            const localizedName =
              lang === "tr"
                ? objInfo.name1 ?? objInfo.name
                : lang === "de"
                ? objInfo.name3 ?? objInfo.name
                : lang === "fr"
                ? objInfo.name4 ?? objInfo.name
                : lang === "es"
                ? objInfo.name5 ?? objInfo.name
                : objInfo.name2 ?? objInfo.name1 ?? objInfo.name;
            if (localizedName) headerName = localizedName;
            else {
              const translationKey = `widgets.${field}`;
              headerName = t(translationKey, { defaultValue: field });
            }
          } else {
            const translationKey = `widgets.${field}`;
            headerName = t(translationKey, { defaultValue: field });
          }
        } else {
          const translationKey = `widgets.${field}`;
          headerName = t(translationKey, { defaultValue: field });
        }
        return {
          field,
          headerName,
          flex: 1,
          minWidth: 90,
          sortable: false,
          headerAlign: "center",
          align: "center",
          renderCell: (params) => {
            const value = params.value;
            return (
              <Typography
                variant="body2"
                sx={{
                  width: "100%",
                  textAlign: "center",
                  fontSize: "0.75rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {typeof value === "number"
                  ? value.toLocaleString()
                  : value ?? "-"}
              </Typography>
            );
          },
        };
      });
    const bankRefColumn = {
      field: "bankRefNo",
      headerName: t("vendingSales.bankRefNo", "Banka Ref No"),
      flex: 1,
      minWidth: 130,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const v = params.value;
        const text =
          v === null || v === undefined || String(v).trim() === "" ? "-" : v;
        return (
          <Typography
            variant="body2"
            sx={{
              width: "100%",
              textAlign: "center",
              fontSize: "0.75rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </Typography>
        );
      },
    };
    return [dateColumn, ...dynamicColumns, bankRefColumn];
  }, [widget.targetObjectList, t, objectData, i18n.language]);

  // Group by modlarına özel sütunlar
  const groupColumns = useMemo(() => {
    const numCell = (params) => {
      const v = params.value;
      return (
        <Typography
          variant="body2"
          sx={{
            width: "100%",
            textAlign: "center",
            fontSize: "0.75rem",
          }}
        >
          {typeof v === "number" ? v.toLocaleString() : v ?? "-"}
        </Typography>
      );
    };
    const amountCell = (params) => {
      const v = params.value;
      const num = Number(v);
      const text = !Number.isNaN(num)
        ? (num / 100).toLocaleString("tr", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) + " ₺"
        : v ?? "-";
      return (
        <Typography
          variant="body2"
          sx={{
            width: "100%",
            textAlign: "center",
            fontSize: "0.75rem",
          }}
        >
          {text}
        </Typography>
      );
    };
    const textCell = (params) => (
      <Typography
        variant="body2"
        sx={{
          width: "100%",
          textAlign: "center",
          fontSize: "0.75rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {params.value ?? "-"}
      </Typography>
    );

    return {
      product: [
        {
          field: "productId",
          headerName: t("vendingSales.product", "Ürün"),
          flex: 2,
          minWidth: 160,
          sortable: false,
          headerAlign: "center",
          align: "center",
          renderCell: textCell,
        },
        {
          field: "salesCount",
          headerName: t("vendingSales.salesCount", "Satış Adedi"),
          flex: 1,
          minWidth: 110,
          sortable: false,
          headerAlign: "center",
          align: "center",
          renderCell: numCell,
        },
        {
          field: "totalAmount",
          headerName: t("vendingSales.totalAmount", "Toplam Tutar"),
          flex: 1,
          minWidth: 130,
          sortable: false,
          headerAlign: "center",
          align: "center",
          renderCell: amountCell,
        },
      ],
      day: [
        {
          field: "date",
          headerName: t("widgets.date", "Tarih"),
          flex: 1,
          minWidth: 130,
          sortable: false,
          headerAlign: "center",
          align: "center",
          renderCell: textCell,
        },
        {
          field: "salesCount",
          headerName: t("vendingSales.salesCount", "Satış Adedi"),
          flex: 1,
          minWidth: 110,
          sortable: false,
          headerAlign: "center",
          align: "center",
          renderCell: numCell,
        },
        {
          field: "totalAmount",
          headerName: t("vendingSales.totalAmount", "Toplam Tutar"),
          flex: 1,
          minWidth: 130,
          sortable: false,
          headerAlign: "center",
          align: "center",
          renderCell: amountCell,
        },
      ],
      nid: [
        {
          field: "NodeName",
          headerName: t("vendingSales.machine", "Otomat"),
          flex: 2,
          minWidth: 160,
          sortable: false,
          headerAlign: "center",
          align: "center",
          renderCell: textCell,
        },
        {
          field: "salesCount",
          headerName: t("vendingSales.salesCount", "Satış Adedi"),
          flex: 1,
          minWidth: 110,
          sortable: false,
          headerAlign: "center",
          align: "center",
          renderCell: numCell,
        },
        {
          field: "totalAmount",
          headerName: t("vendingSales.totalAmount", "Toplam Tutar"),
          flex: 1,
          minWidth: 130,
          sortable: false,
          headerAlign: "center",
          align: "center",
          renderCell: amountCell,
        },
      ],
    };
  }, [t]);

  const displayColumns = useMemo(() => {
    if (groupMode === "product") return groupColumns.product;
    if (groupMode === "day") return groupColumns.day;
    if (groupMode === "nid") return groupColumns.nid;
    return columns;
  }, [groupMode, groupColumns, columns]);

  const displayRows = useMemo(() => {
    if (groupMode === "product") {
      return groupByProductData.map((r, idx) => ({
        id: `p-${r.productId ?? idx}-${idx}`,
        ...r,
      }));
    }
    if (groupMode === "day") {
      return groupByDayData.map((r, idx) => ({
        id: `d-${r.date ?? idx}-${idx}`,
        ...r,
      }));
    }
    if (groupMode === "nid") {
      return groupByNidData.map((r, idx) => ({
        id: `n-${r.NID ?? idx}-${idx}`,
        ...r,
      }));
    }
    return rows;
  }, [groupMode, rows, groupByProductData, groupByDayData, groupByNidData]);

  // Dönen veriyi satıra çevir
  const convertValue = useMemo(
    () => (codeStr, rawVal) => {
      if (rawVal === null || rawVal === undefined) return null;
      if (codeStr === "NID") return rawVal; // Node ID olduğu varsayımı
      const numericCode = parseInt(codeStr, 10);
      if (isNaN(numericCode)) return rawVal;
      const objInfo = objectData.find((o) => o.code === numericCode);
      if (!objInfo) return rawVal;
      if (
        widget.isConvertableObject === false ||
        objInfo.isConvertableObject === false
      ) {
        return rawVal;
      }
      try {
        return ConvertOIDValueToString({ Type: objInfo.type }, rawVal);
      } catch (e) {
        return rawVal;
      }
    },
    [objectData, widget.isConvertableObject]
  );

  useEffect(() => {
    // Grup modlarında getSaleList çağrılmamalı (grup endpoint'leri ayrı effect'te çalışıyor)
    if (groupMode !== "default") {
      setLoading(false);
      return;
    }
    async function fetchList() {
      setLoading(true);
      setError(null);
      try {
        // Makine NID listesini hazırla
        let nodeList = [];
        if (selectedMachine && selectedMachine.length > 0 && machines && machines.length > 0) {
          nodeList = machines
            .filter((m) => selectedMachine.includes(m.name))
            .map((m) => m.idNode)
            .filter(Boolean);
        } else if (machines && machines.length > 0) {
          nodeList = machines.map((m) => m.idNode).filter(Boolean);
        }

        // timePeriod hesapla
        const timePeriod =
          selectedTimePeriod !== null
            ? selectedTimePeriod
            : startEpoch !== null || endEpoch !== null
            ? TimePeriod.Unfiltered
            : TimePeriod.Today;

        const saleOptions = {
          ...(startEpoch !== null && { start: startEpoch }),
          ...(endEpoch !== null && { end: endEpoch }),
          count: 0,
          timePeriod,
          val: Array.isArray(selectedPaymentTypes) ? selectedPaymentTypes : [],
        };

        const data = await getSaleList(nodeList, saleOptions);
        // Beklenen yapı varsayımı:
        // 1) Array of objects [{ NID: 1, "1000": 123, "1001": 456, ... }, ...]
        // 2) Veya tek obje { rows: [...]} vs. Bu yüzden birkaç varyasyon ele alınıyor.
        let records = [];
        if (Array.isArray(data)) {
          records = data;
        } else if (data && Array.isArray(data.rows)) {
          records = data.rows;
        } else if (data && Array.isArray(data.data)) {
          records = data.data; // alternatif
        } else {
          records = [];
        }
        // Epoch değerine göre (en yeni üstte) sırala
        records.sort((a, b) => {
          const getEpoch = (rec) => {
            let v = rec?.epoch ?? rec?.Epoch ?? rec?.timestamp ?? rec?.time;
            if (v === undefined || v === null || v === "") return -Infinity;
            let num = Number(v);
            if (Number.isNaN(num)) return -Infinity;
            // saniye ise ms'e çevir
            if (num < 1e12) num *= 1000;
            return num;
          };
          return getEpoch(b) - getEpoch(a);
        });
        // Kayıttan değer bulma (root key veya paramList.oid)
        const getValueFromRecord = (rec, code) => {
          if (!rec || typeof rec !== "object") return null;
          // Önce kök anahtarlar
          if (Object.prototype.hasOwnProperty.call(rec, code)) {
            return rec[code];
          }
          // Param listesinde ara: {oid, val}
          const numCode = Number(code);
          if (!Number.isNaN(numCode) && Array.isArray(rec.paramList)) {
            const found = rec.paramList.find((p) => Number(p?.oid) === numCode);
            if (found && found.val !== undefined) return found.val;
          }
          return null;
        };

        const coerceNumberIfPossible = (v) => {
          if (v === null || v === undefined) return v;
          if (typeof v === "number") return v;
          if (typeof v === "string" && v.trim() !== "") {
            const n = Number(v);
            if (!Number.isNaN(n)) return n;
          }
          return v;
        };

        // Otomat seçimine göre kayıtları filtrele (istatistikler için)
        // Eğer otomat seçimi varsa onları kullan
        // Eğer otomat seçimi yoksa ama bölge/alt bölge seçimi varsa, filtrelenmiş makineleri kullan
        let filteredMachineNames = selectedMachine;
        if (selectedMachine.length === 0 && machines && machines.length > 0) {
          // Bölge filtresi aktifse, machines zaten filtrelenmiş durumda
          filteredMachineNames = machines.map(m => m.name);
        }

        const recordsForStats =
          filteredMachineNames.length > 0
            ? records.filter((rec) => filteredMachineNames.includes(rec?.NodeName))
            : records;

        // Satışları ödeme tipine göre hesapla
        // 1011: 307 = Nakit, 309 = Mobil, 314 = Kart
        let totalCount = 0;
        let totalAmount = 0;
        let cashCount = 0;
        let cashAmount = 0;
        let mobileCount = 0;
        let mobileAmount = 0;
        let cardCount = 0;
        let cardAmount = 0;

        recordsForStats.forEach((rec) => {
          const paymentType = rec?.val ?? getValueFromRecord(rec, "1011");
          const amount = getValueFromRecord(rec, "1004");

          let numAmount = 0;
          if (amount !== null && amount !== undefined) {
            numAmount = Number(amount);
            if (Number.isNaN(numAmount)) {
              numAmount = 0;
            }
          }

          if (paymentType === 307) {
            // Nakit
            cashCount++;
            cashAmount += numAmount;
            totalCount++;
            totalAmount += numAmount;
          } else if (paymentType === 309) {
            // Mobil
            mobileCount++;
            mobileAmount += numAmount;
            totalCount++;
            totalAmount += numAmount;
          } else if (paymentType === 314) {
            // Kart
            cardCount++;
            cardAmount += numAmount;
            totalCount++;
            totalAmount += numAmount;
          }
        });

        setTotalSalesCount(totalCount);
        setTotalSalesAmount(totalAmount);
        setCashSalesCount(cashCount);
        setCashSalesAmount(cashAmount);
        setMobileSalesCount(mobileCount);
        setMobileSalesAmount(mobileAmount);
        setCardSalesCount(cardCount);
        setCardSalesAmount(cardAmount);

        // En çok satış yapan otomat hesapla (NID'ye göre)
        const machinesSales = {};
        const machineNames = {}; // NID -> NodeName mapping

        recordsForStats.forEach((rec) => {
          const nid = rec?.NID;
          const nodeName = rec?.NodeName; // NodeName direkt record'da geliyor!
          const paymentType = rec?.val ?? getValueFromRecord(rec, "1011");
          const amount = getValueFromRecord(rec, "1004");

          // Sadece geçerli ödeme tiplerini hesapla (307, 309, 314)
          if (
            nid &&
            (paymentType === 307 || paymentType === 309 || paymentType === 314)
          ) {
            let numAmount = 0;
            if (amount !== null && amount !== undefined) {
              numAmount = Number(amount);
              if (Number.isNaN(numAmount)) {
                numAmount = 0;
              }
            }

            if (!machinesSales[nid]) {
              machinesSales[nid] = 0;
            }
            machinesSales[nid] += numAmount;

            // NodeName'i sakla
            if (nodeName && !machineNames[nid]) {
              machineNames[nid] = nodeName;
            }
          }
        });

        // En yüksek tutarı bul
        let topNID = null;
        let topAmount = 0;
        Object.entries(machinesSales).forEach(([nid, amount]) => {
          if (amount > topAmount) {
            topAmount = amount;
            topNID = nid;
          }
        });

        // NodeName'i bul
        const machineName =
          topNID && machineNames[topNID]
            ? machineNames[topNID]
            : topNID
            ? `Otomat ${topNID}`
            : "Veri Yok";

        setTopMachineName(machineName);
        setTopMachineAmount(topAmount);

        // Adet bazında en çok satış yapan otomat ve en çok satılan ürün (1002)
        const machinesSalesCount = {};
        const machinesProducts = {}; // NID -> { product: count }

        recordsForStats.forEach((rec) => {
          const nid = rec?.NID;
          const nodeName = rec?.NodeName;
          const paymentType = rec?.val ?? getValueFromRecord(rec, "1011");
          const productCode = getValueFromRecord(rec, "1002"); // Ürün kodu

          // Sadece geçerli ödeme tiplerini hesapla (307, 309, 314)
          if (
            nid &&
            (paymentType === 307 || paymentType === 309 || paymentType === 314)
          ) {
            // Satış sayısı
            if (!machinesSalesCount[nid]) {
              machinesSalesCount[nid] = {
                count: 0,
                name: nodeName || `Otomat ${nid}`,
              };
            }
            machinesSalesCount[nid].count++;

            // Ürün sayıları
            if (productCode !== null && productCode !== undefined) {
              if (!machinesProducts[nid]) {
                machinesProducts[nid] = {};
              }
              if (!machinesProducts[nid][productCode]) {
                machinesProducts[nid][productCode] = 0;
              }
              machinesProducts[nid][productCode]++;
            }
          }
        });

        // En çok satış adedi olan otomata göre bul
        let topCountNID = null;
        let topCount = 0;
        Object.entries(machinesSalesCount).forEach(([nid, data]) => {
          if (data.count > topCount) {
            topCount = data.count;
            topCountNID = nid;
          }
        });

        // Bu otomatın en çok sattığı ürünü bul
        let topProduct = null;
        let topProductQty = 0;
        if (topCountNID && machinesProducts[topCountNID]) {
          Object.entries(machinesProducts[topCountNID]).forEach(
            ([product, count]) => {
              if (count > topProductQty) {
                topProductQty = count;
                topProduct = product;
              }
            }
          );
        }

        // Seçim kodunu direkt göster (1002'nin value'su olduğu gibi)
        const selectionDisplay = topProduct || "-";

        setTopCountMachineName(
          topCountNID && machinesSalesCount[topCountNID]
            ? machinesSalesCount[topCountNID].name
            : "Veri Yok"
        );
        setTopProductName(selectionDisplay); // Artık sadece seçim kodu (ör: 43, 48, 62)
        setTopProductCount(topProductQty);

        // Zamana göre kümülatif satış grafiği verisi oluştur (filtrelenmiş veriler)
        const chartData = [];
        const sortedRecords = [...recordsForStats]
          .filter((rec) => {
            const paymentType = rec?.val ?? getValueFromRecord(rec, "1011");
            return (
              paymentType === 307 || paymentType === 309 || paymentType === 314
            );
          })
          .reverse(); // Eskiden yeniye sırala

        let cumulativeAmount = 0; // Kümülatif toplam
        sortedRecords.forEach((rec) => {
          const amount = getValueFromRecord(rec, "1004");
          if (amount !== null && amount !== undefined) {
            const numAmount = Number(amount);
            if (!Number.isNaN(numAmount)) {
              // Kuruş cinsinden TL'ye çevir ve kümülatif toplama ekle
              cumulativeAmount += numAmount / 100;
              chartData.push(cumulativeAmount);
            }
          }
        });

        setSalesChartData(chartData.length > 0 ? chartData : [0]);

        // Otomat seçimine göre filtreleme
        // Eğer otomat seçimi varsa onları kullan
        // Eğer otomat seçimi yoksa ama bölge/alt bölge seçimi varsa, filtrelenmiş makineleri kullan
        let filteredRecords = records;

        if (selectedMachine.length > 0) {
          // Kullanıcı spesifik otomatlar seçmişse
          filteredRecords = records.filter((rec) => selectedMachine.includes(rec?.NodeName));
        } else if ((selectedRegion.length > 0 || selectedSubRegion.length > 0) && machines && machines.length > 0) {
          // Kullanıcı otomat seçmemiş ama bölge/alt bölge seçmişse
          // machines dizisi zaten filtrelenmiş durumda, o otomatlara ait kayıtları göster
          const filteredMachineNames = machines.map(m => m.name);
          filteredRecords = records.filter((rec) => filteredMachineNames.includes(rec?.NodeName));
        }

        const processed = filteredRecords.map((rec, idx) => {
          // Benzersiz ID: _id varsa onu kullan, yoksa NID-epoch-idx
          const rawEpoch =
            rec?.epoch ?? rec?.Epoch ?? rec?.timestamp ?? rec?.time;
          const id = rec?._id ?? `${rec?.NID ?? "n"}-${rawEpoch ?? "t"}-${idx}`;
          const row = {
            id,
            NID: rec?.NID, // Navigation için NID'yi row'a ekle
          };

          // Tarih sütunu
          if (rawEpoch !== undefined && rawEpoch !== null && rawEpoch !== "") {
            let epochNum = Number(rawEpoch);
            if (!Number.isNaN(epochNum)) {
              if (epochNum < 1e12) epochNum *= 1000; // saniye -> ms
              const d = new Date(epochNum);
              const isValid = !isNaN(d.getTime());
              row.date = isValid
                ? d.toLocaleString(undefined, {
                    year: "2-digit",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-";
            } else {
              row.date = "-";
            }
          } else {
            row.date = null;
          }

          // Dinamik sütun değerleri
          if (Array.isArray(widget.targetObjectList)) {
            widget.targetObjectList.forEach((code) => {
              if (code === "NID") return; // NID gösterilmiyor
              // 1011 kodu için val alanını kullan ve enum karşılığını göster
              const codeStr = code.toString();
              let rawVal;
              if (codeStr === "1011") {
                rawVal = rec?.val;
              } else {
                rawVal = getValueFromRecord(rec, code);
              }
              const maybeNumber = coerceNumberIfPossible(rawVal);
              row[code] = convertValue(code, maybeNumber);
            });
          }
          // Banka Ref No (OID 223) — targetObjectList'te olmasa da her zaman ekle
          row.bankRefNo = getValueFromRecord(rec, "223");
          return row;
        });
        setRows(processed);
      } catch (e) {
        console.error("WidgetList fetch error", e);
        setError(e.message || "Veri alınamadı");
        setRows([]);
      } finally {
        setLoading(false);
      }
    }
    fetchList();
  }, [
    widget,
    objectData,
    convertValue,
    startEpoch,
    endEpoch,
    selectedTimePeriod,
    selectedMachine,
    selectedRegion,
    selectedSubRegion,
    machines,
    i18n.language,
    selectedPaymentTypes,
    groupMode,
  ]);

  return (
    <Paper
      elevation={2}
      sx={(theme) => ({
        p: 0.5,
        borderRadius: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginBottom: 2,
        backgroundColor: theme.palette.background.paper,
        position: "relative",
        border: `1px solid ${theme.palette.divider}`,
      })}
    >
      {groupMode === "default" && (
      <Box
        sx={{
          display: "flex",
          gap: 1,
          px: 2,
          py: 1.5,
          justifyContent: "stretch",
          flexWrap: "wrap",
        }}
      >
        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor:
              theme.palette.mode === "dark" ? "#1e4d2b" : "#e8f5e9",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              display: "block",
              mb: 0.5,
              fontSize: "0.9rem",
            }}
          >
            {t("vendingSales.totalSalesAmount", "Toplam Satış Tutarı")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#2e7d32" }}>
            {(totalSalesAmount / 100).toLocaleString("tr", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            ₺
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor:
              theme.palette.mode === "dark" ? "#1e4d2b" : "#e8f5e9",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              display: "block",
              mb: 0.5,
              fontSize: "0.9rem",
            }}
          >
            {t("vendingSales.cardSalesAmount", "Kart Satış Tutarı")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#2e7d32" }}>
            {(cardSalesAmount / 100).toLocaleString("tr", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            ₺
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor:
              theme.palette.mode === "dark" ? "#1e4d2b" : "#e8f5e9",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              display: "block",
              mb: 0.5,
              fontSize: "0.9rem",
            }}
          >
            {t("vendingSales.cashSalesAmount", "Nakit Satış Tutarı")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#2e7d32" }}>
            {(cashSalesAmount / 100).toLocaleString("tr", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            ₺
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor:
              theme.palette.mode === "dark" ? "#1e4d2b" : "#e8f5e9",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              display: "block",
              mb: 0.5,
              fontSize: "0.9rem",
            }}
          >
            {t("vendingSales.mobileSalesAmount", "Mobil Satış Tutarı")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#2e7d32" }}>
            {(mobileSalesAmount / 100).toLocaleString("tr", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            ₺
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor:
              theme.palette.mode === "dark" ? "#1a237e" : "#e3f2fd",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              display: "block",
              mb: 0.5,
              fontSize: "0.9rem",
            }}
          >
            {t("vendingSales.totalSalesCount", "Toplam Satış Adedi")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1565c0" }}>
            {totalSalesCount.toLocaleString()}
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor:
              theme.palette.mode === "dark" ? "#1a237e" : "#e3f2fd",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              display: "block",
              mb: 0.5,
              fontSize: "0.9rem",
            }}
          >
            {t("vendingSales.cardSalesCount", "Kart Satış Adedi")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1565c0" }}>
            {cardSalesCount.toLocaleString()}
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor:
              theme.palette.mode === "dark" ? "#1a237e" : "#e3f2fd",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              display: "block",
              mb: 0.5,
              fontSize: "0.9rem",
            }}
          >
            {t("vendingSales.cashSalesCount", "Nakit Satış Adedi")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1565c0" }}>
            {cashSalesCount.toLocaleString()}
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor:
              theme.palette.mode === "dark" ? "#1a237e" : "#e3f2fd",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              display: "block",
              mb: 0.5,
              fontSize: "0.9rem",
            }}
          >
            {t("vendingSales.mobileSalesCount", "Mobil Satış Adedi")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1565c0" }}>
            {mobileSalesCount.toLocaleString()}
          </Typography>
        </Paper>
      </Box>
      )}

      {groupMode === "default" && (
      <Box
        sx={{
          display: "flex",
          gap: 2,
          px: 2,
          py: 2,
          justifyContent: "stretch",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <HighlightedCard
            topMachineName={topMachineName}
            topMachineAmount={topMachineAmount}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 200 }}>
          <TopProductCard
            topCountMachineName={topCountMachineName}
            topProductName={topProductName}
            topProductCount={topProductCount}
          />
        </Box>
      </Box>
      )}

      {groupMode !== "default" && (
        <Box sx={{ px: 2, py: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setChartsHidden((v) => !v)}
              sx={{
                textTransform: "none",
                borderRadius: 1,
                fontWeight: 500,
              }}
            >
              {chartsHidden
                ? t("vendingSales.showCharts", "Grafikleri Göster")
                : t("vendingSales.hideCharts", "Grafikleri Gizle")}
            </Button>
          </Box>
          {!chartsHidden && (() => {
            let labels = [];
            let amounts = [];
            let counts = [];
            let titleLabel = "";
            if (groupMode === "product") {
              const top = [...groupByProductData]
                .sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0))
                .slice(0, 15);
              labels = top.map((r) => r.productId ?? "-");
              amounts = top.map((r) => Number(r.totalAmount || 0) / 100);
              counts = top.map((r) => Number(r.salesCount || 0));
              titleLabel = t("vendingSales.viewByProduct", "Ürüne Göre");
            } else if (groupMode === "day") {
              const sorted = [...groupByDayData].sort((a, b) =>
                String(a.date).localeCompare(String(b.date))
              );
              labels = sorted.map((r) => r.date ?? "-");
              amounts = sorted.map((r) => Number(r.totalAmount || 0) / 100);
              counts = sorted.map((r) => Number(r.salesCount || 0));
              titleLabel = t("vendingSales.viewByDay", "Güne Göre");
            } else if (groupMode === "nid") {
              const top = [...groupByNidData]
                .sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0))
                .slice(0, 15);
              labels = top.map((r) => r.NodeName ?? `NID ${r.NID}`);
              amounts = top.map((r) => Number(r.totalAmount || 0) / 100);
              counts = top.map((r) => Number(r.salesCount || 0));
              titleLabel = t("vendingSales.viewByMachine", "Otomata Göre");
            }

            if (labels.length <= 1) {
              return null;
            }

            const amountLabel =
              t("vendingSales.totalAmount", "Toplam Tutar") + " (₺)";
            const countLabel = t("vendingSales.salesCount", "Satış Adedi");
            const fmtAmountTR = (v) =>
              v == null
                ? ""
                : Number(v).toLocaleString("tr", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) + " ₺";
            const xAxisCfg = [
              {
                scaleType: "band",
                data: labels,
                tickLabelStyle: { fontSize: 11 },
              },
            ];
            const commonMargin = { left: 75, right: 25, top: 30, bottom: 90 };
            // Toplamlar, grafiğe çizilen (ilk 15'e kırpılmış) amounts/counts
            // dizilerinden DEĞİL, tüm grup verisinden hesaplanmalı; aksi halde
            // top-15 dışındaki ürün/otomat satışları toplama girmez ve değer
            // gerçeğin altında kalır.
            const fullGroupData =
              groupMode === "product"
                ? groupByProductData
                : groupMode === "day"
                ? groupByDayData
                : groupMode === "nid"
                ? groupByNidData
                : [];
            const totalAmountSum =
              fullGroupData.reduce((s, r) => s + Number(r.totalAmount || 0), 0) /
              100;
            const totalCountSum = fullGroupData.reduce(
              (s, r) => s + Number(r.salesCount || 0),
              0
            );
            return (
              <Box sx={{ width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 8,
                    mb: 2,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle1" sx={{ color: "#2e7d32", fontWeight: 700 }}>
                      {amountLabel}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {fmtAmountTR(totalAmountSum)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle1" sx={{ color: "#1565c0", fontWeight: 700 }}>
                      {countLabel}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {Number(totalCountSum).toLocaleString("tr", { maximumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                </Box>
                <BarChart
                  height={380}
                  xAxis={xAxisCfg}
                  yAxis={[
                    {
                      id: "amount",
                      valueFormatter: (v) =>
                        Number(v).toLocaleString("tr", {
                          maximumFractionDigits: 0,
                        }) + " ₺",
                    },
                    {
                      id: "count",
                      valueFormatter: (v) =>
                        Number(v).toLocaleString("tr", {
                          maximumFractionDigits: 0,
                        }),
                    },
                  ]}
                  rightAxis="count"
                  leftAxis="amount"
                  series={[
                    {
                      type: "bar",
                      data: amounts,
                      label: amountLabel,
                      color: "#2e7d32",
                      yAxisId: "amount",
                      valueFormatter: fmtAmountTR,
                    },
                    {
                      type: "line",
                      data: counts,
                      label: countLabel,
                      color: "#1565c0",
                      yAxisId: "count",
                      curve: "monotoneX",
                      showMark: true,
                    },
                  ]}
                  margin={{ left: 80, right: 70, top: 40, bottom: 90 }}
                  grid={{ horizontal: true }}
                  slotProps={{
                    legend: { hidden: true },
                  }}
                >
                  <LinePlot />
                  <MarkPlot />
                </BarChart>
              </Box>
            );
          })()}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          height: "auto",
        }}
      >
        {/* Pagination ile DataGrid */}
        <DataGrid
          rows={displayRows}
          columns={displayColumns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[20]}
          paginationMode="client"
          density="compact"
          loading={loading}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          onRowClick={(params) => {
            if (groupMode !== "default") return;
            const nid = params.row.NID;
            if (nid) {
              window.open(`/vending-machines/${nid}`, "_blank");
            }
          }}
          sx={{
            height: "100%",
            overflow: "unset",
            border: "none",
            "& .MuiDataGrid-main": {
              height: "100%",
              overflowX: "auto",
              minWidth: { xs: "700px", sm: "100%" },
            },
            "& .MuiDataGrid-virtualScroller": {
              height: "100%",
              overflowY: "auto",
              overflowX: "auto",
            },
            "& .MuiDataGrid-columnHeaders": {
              color: (theme) => theme.palette.text.primary,
              minHeight: HEADER_HEIGHT,
              maxHeight: HEADER_HEIGHT,
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
              padding: "0 4px",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark" ? "#29434e" : "#e0e0e0"
                }`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1.1,
            },
            "& .MuiDataGrid-row": {
              alignItems: "center !important",
              cursor: groupMode === "default" ? "pointer" : "default",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.04)",
              },
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              width: "100%",
              textAlign: "center",
              lineHeight: 1.2,
            },
            "& .MuiDataGrid-footerContainer": {
              minHeight: FOOTER_HEIGHT,
            },
          }}
          getRowId={(row) => row.id}
        />
        {error && !loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(0,0,0,0.4)"
                  : "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              fontWeight: 500,
              color: "error.main",
            }}
          >
            {error}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default function ObjectDetails() {
  const { t } = useTranslation();
  const [widgetData, setWidgetData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filtre state'leri - Başlangıçta "Bugün" seçili (timePeriod: 7)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startEpoch, setStartEpoch] = useState(null);
  const [endEpoch, setEndEpoch] = useState(null);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(TimePeriod.Today);
  const [selectedMachine, setSelectedMachine] = useState([]); // Seçilen otomatlar (multi-select)
  const [machines, setMachines] = useState([]); // Otomat listesi
  const [machineSearchText, setMachineSearchText] = useState(""); // Dropdown içi arama
  const [selectedRegion, setSelectedRegion] = useState([]); // Seçilen Level 2 bölgeler (multi-select)
  const [regions, setRegions] = useState([]); // Bölge listesi (Level 2 bölgeler gösterilir)
  const [allRegions, setAllRegions] = useState([]); // Tüm bölgeler (filtrelenmemiş)
  const [regionSearchText, setRegionSearchText] = useState(""); // Bölge dropdown içi arama
  const [selectedSubRegion, setSelectedSubRegion] = useState([]); // Seçilen Level 3 alt bölgeler (multi-select)
  const [subRegions, setSubRegions] = useState([]); // Alt bölge listesi (Level 3 bölgeler gösterilir)
  const [subRegionSearchText, setSubRegionSearchText] = useState(""); // Alt bölge dropdown içi arama
  const [allMachines, setAllMachines] = useState([]); // Tüm otomatlar (filtrelenmemiş)

  // "Uygula" butonuna basılınca güncellenen applied state'ler
  const [appliedMachine, setAppliedMachine] = useState([]);
  const [appliedRegion, setAppliedRegion] = useState([]);
  const [appliedSubRegion, setAppliedSubRegion] = useState([]);
  const [appliedMachines, setAppliedMachines] = useState([]); // filtrelenmiş machines listesi
  const [dataLoading, setDataLoading] = useState(false); // WidgetList veri yükleme durumu
  const [pendingPrevMonthExport, setPendingPrevMonthExport] = useState(false);
  const [pendingPrevWeekExport, setPendingPrevWeekExport] = useState(false);
  const [groupMode, setGroupMode] = useState("day");
  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState([]); // [307, 308, 309]
  const [appliedPaymentTypes, setAppliedPaymentTypes] = useState([]);

  const PAYMENT_TYPES = React.useMemo(
    () => [
      { value: 307, label: t("vendingSales.paymentCash", "Nakit") },
      { value: 314, label: t("vendingSales.paymentCard", "Kart") },
      { value: 309, label: t("vendingSales.paymentMobile", "Mobil") },
    ],
    [t]
  );

  // Excel export fonksiyonlarını tutmak için ref'ler
  const exportFunctionRef = React.useRef(null);
  const exportTableFunctionRef = React.useRef(null);

  // Export fonksiyonlarını WidgetList'ten al
  const handleSetExportFunction = React.useCallback((exportFn) => {
    exportFunctionRef.current = exportFn;
  }, []);

  const handleSetTableExportFunction = React.useCallback((exportFn) => {
    exportTableFunctionRef.current = exportFn;
  }, []);

  // Aylık satış raporu butonuna tıklandığında
  const handleExportToExcel = React.useCallback(() => {
    if (exportFunctionRef.current) {
      exportFunctionRef.current();
    } else {
      alert("Export fonksiyonu hazır değil.");
    }
  }, []);

  // Tablodaki satış raporu butonuna tıklandığında
  const handleExportTableToExcel = React.useCallback(() => {
    if (exportTableFunctionRef.current) {
      exportTableFunctionRef.current();
    } else {
      alert("Export fonksiyonu hazır değil.");
    }
  }, []);

  // Use custom hook to get objectData from localStorage
  const objectData = useObjectData();

  // Bir bölgenin tüm alt bölgelerini (children) bulan fonksiyon
  const getAllChildRegions = React.useCallback(
    (parentRegionId, allRegionsData) => {
      const childRegions = allRegionsData.filter(
        (r) => r.parentRegion === parentRegionId
      );
      let allChildren = [...childRegions];

      // Recursive olarak tüm alt bölgeleri bul
      childRegions.forEach((child) => {
        allChildren = [
          ...allChildren,
          ...getAllChildRegions(child.idRegion, allRegionsData),
        ];
      });

      return allChildren;
    },
    []
  );

  // Bölge listesini çek ve sadece level 2 olanları göster
  useEffect(() => {
    async function fetchRegions() {
      try {
        const regionsData = await getRegion();
        if (regionsData && Array.isArray(regionsData)) {
          // Tüm bölgeleri sakla (alt bölge kontrolü için)
          setAllRegions(regionsData);

          // Sadece level 2 bölgeleri göster
          const level2Regions = regionsData.filter(
            (region) => getRegionLevel(region, regionsData) === 2
          );
          setRegions(level2Regions);
        }
      } catch (error) {
        console.error("Error fetching regions:", error);
      }
    }
    fetchRegions();
  }, []);

  // Otomat listesini çek
  useEffect(() => {
    async function fetchMachines() {
      try {
        const nodesData = await getNode();
        if (nodesData && Array.isArray(nodesData)) {
          setAllMachines(nodesData);
          setMachines(nodesData);
        }
      } catch (error) {
        console.error("Error fetching nodes:", error);
      }
    }
    fetchMachines();
  }, []);

  // Level 2 bölge seçimine göre Level 3 alt bölgeleri güncelle
  useEffect(() => {
    if (selectedRegion.length > 0 && allRegions.length > 0) {
      // Seçilen Level 2 bölgelerin direkt alt bölgelerini (Level 3) bul
      let level3Regions = [];

      selectedRegion.forEach((level2RegionId) => {
        // Sadece direkt children'ları al (Level 3)
        const directChildren = allRegions.filter(
          (r) => r.parentRegion === level2RegionId
        );
        level3Regions = [...level3Regions, ...directChildren];
      });

      setSubRegions(level3Regions);

      // Seçili alt bölgeleri temizle
      setSelectedSubRegion([]);
    } else {
      // Bölge seçimi yoksa alt bölgeleri temizle
      setSubRegions([]);
      setSelectedSubRegion([]);
    }
  }, [selectedRegion, allRegions]);

  // Alt bölge (Level 3) seçimine göre otomatları filtrele
  useEffect(() => {
    if (selectedSubRegion.length > 0 && allRegions.length > 0) {
      // Seçilen Level 3 bölgelerin tüm alt bölgelerini (Level 4, 5, vs.) bul
      let allSubRegionIds = [...selectedSubRegion];

      selectedSubRegion.forEach((level3RegionId) => {
        const subChildren = getAllChildRegions(level3RegionId, allRegions);
        const subChildrenIds = subChildren.map((r) => r.idRegion);
        allSubRegionIds = [...allSubRegionIds, ...subChildrenIds];
      });

      // Bu bölgelere ait otomatları filtrele
      const filteredMachines = allMachines.filter((machine) =>
        allSubRegionIds.includes(machine.region_idRegion)
      );
      setMachines(filteredMachines);

      // Eğer seçili otomatlar filtrelenmiş listede yoksa temizle
      setSelectedMachine((prev) =>
        prev.filter((machineName) =>
          filteredMachines.some((m) => m.name === machineName)
        )
      );
    } else if (selectedRegion.length > 0 && allRegions.length > 0) {
      // Alt bölge seçimi yoksa ama ana bölge seçiliyse, tüm Level 3 bölgelerdeki otomatları göster
      let level3RegionIds = [];

      selectedRegion.forEach((level2RegionId) => {
        // Sadece direkt children'ları al (Level 3)
        const directChildren = allRegions.filter(
          (r) => r.parentRegion === level2RegionId
        );
        const directChildrenIds = directChildren.map((r) => r.idRegion);
        level3RegionIds = [...level3RegionIds, ...directChildrenIds];

        // Level 3'lerin de alt bölgeleri varsa onları da ekle (Level 4, 5, vs.)
        directChildren.forEach((level3Region) => {
          const subChildren = getAllChildRegions(
            level3Region.idRegion,
            allRegions
          );
          const subChildrenIds = subChildren.map((r) => r.idRegion);
          level3RegionIds = [...level3RegionIds, ...subChildrenIds];
        });
      });

      // Level 3 ve alt bölgelerine ait otomatları filtrele
      const filteredMachines = allMachines.filter((machine) =>
        level3RegionIds.includes(machine.region_idRegion)
      );
      setMachines(filteredMachines);

      // Eğer seçili otomatlar filtrelenmiş listede yoksa temizle
      setSelectedMachine((prev) =>
        prev.filter((machineName) =>
          filteredMachines.some((m) => m.name === machineName)
        )
      );
    } else {
      // Hiçbir bölge seçimi yoksa tüm otomatları göster
      setMachines(allMachines);
    }
  }, [selectedSubRegion, selectedRegion, allMachines, allRegions, getAllChildRegions]);

  useEffect(() => {
    async function fetchWidgets() {
      setLoading(true);
      try {
        const projectCode = localStorage.getItem("projectCode");
        const projectData = await getProjects();
        const currentProject = projectData.find(
          (project) => project.code === projectCode
        );

        if (currentProject && currentProject.projectDashboardWidgetJson) {
          const widgets = JSON.parse(currentProject.projectDashboardWidgetJson);
          setWidgetData(widgets);
        } else {
          console.error("No widgets found for the current project.");
          setWidgetData({ widgets: [] });
        }
      } catch (error) {
        console.error("Error fetching widgets:", error);
        setWidgetData({ widgets: [] });
      } finally {
        setLoading(false);
      }
    }
    fetchWidgets();
  }, []);

  const hasWidgets =
    widgetData &&
    Array.isArray(widgetData.widgets) &&
    widgetData.widgets.length > 0;

  // Filtre uygula fonksiyonu (Custom tarih)
  const handleApplyFilter = () => {
    if (startDate) {
      const startTimestamp = Math.floor(new Date(startDate).getTime() / 1000);
      setStartEpoch(startTimestamp);
    } else {
      setStartEpoch(null);
    }

    if (endDate) {
      const endTimestamp = Math.floor(new Date(endDate).getTime() / 1000);
      setEndEpoch(endTimestamp);
    } else {
      setEndEpoch(null);
    }

    setSelectedTimePeriod(null);
    // Bölge ve otomat seçimlerini uygula
    setAppliedMachine([...selectedMachine]);
    setAppliedRegion([...selectedRegion]);
    setAppliedSubRegion([...selectedSubRegion]);
    setAppliedMachines([...machines]);
    setAppliedPaymentTypes([...selectedPaymentTypes]);
  };

  const handleQuickFilter = (timePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

    let start, end;

    if (timePeriod === TimePeriod.Today) {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      end = now;
    } else if (timePeriod === TimePeriod.Yesterday) {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
    } else if (timePeriod === TimePeriod.ThisWeek) {
      const dayOfWeek = now.getDay();
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diffToMonday, 0, 0, 0);
      end = now;
    } else if (timePeriod === TimePeriod.LastWeek) {
      const dayOfWeek = now.getDay();
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const thisMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diffToMonday, 0, 0, 0);
      start = new Date(thisMonday.getTime() - 7 * 86400 * 1000);
      end = new Date(thisMonday.getTime() - 1000);
      end.setHours(23, 59, 59);
    } else if (timePeriod === TimePeriod.ThisMonth) {
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      end = now;
    } else if (timePeriod === TimePeriod.LastMonth) {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    }

    if (start && end) {
      setStartDate(toDateStr(start));
      setEndDate(toDateStr(end));
      setStartEpoch(Math.floor(start.getTime() / 1000));
      setEndEpoch(Math.floor(end.getTime() / 1000));
    }

    // Bölge ve otomat seçimlerini uygula
    setAppliedMachine([...selectedMachine]);
    setAppliedRegion([...selectedRegion]);
    setAppliedSubRegion([...selectedSubRegion]);
    setAppliedMachines([...machines]);
  };


  const handlePreviousWeekExport = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const thisMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diffToMonday, 0, 0, 0);
    const lastMonday = new Date(thisMonday.getTime() - 7 * 86400 * 1000);
    const lastSunday = new Date(thisMonday.getTime() - 1000);
    lastSunday.setHours(23, 59, 59);

    const pad = (n) => String(n).padStart(2, "0");
    const startStr = `${lastMonday.getFullYear()}-${pad(lastMonday.getMonth() + 1)}-${pad(lastMonday.getDate())}T00:00`;
    const endStr = `${lastSunday.getFullYear()}-${pad(lastSunday.getMonth() + 1)}-${pad(lastSunday.getDate())}T23:59`;

    setStartDate(startStr);
    setEndDate(endStr);
    setSelectedTimePeriod(null);
    setPendingPrevWeekExport(true);
  };

  const handlePreviousMonthExport = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

    const startStr = `${firstDay.getFullYear()}-${String(firstDay.getMonth() + 1).padStart(2, "0")}-01T00:00`;
    const endStr = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}T23:59`;

    // Sadece tarihleri set et, filtre uygulama (ekranda veri çekme)
    setStartDate(startStr);
    setEndDate(endStr);
    setSelectedTimePeriod(null);
    setPendingPrevMonthExport(true);
  };

  // Tarihler güncellenince export tetikle (tek istek)
  useEffect(() => {
    if (pendingPrevMonthExport) {
      setPendingPrevMonthExport(false);
      handleExportToExcel();
    }
  }, [pendingPrevMonthExport, handleExportToExcel]);

  useEffect(() => {
    if (pendingPrevWeekExport) {
      setPendingPrevWeekExport(false);
      handleExportToExcel();
    }
  }, [pendingPrevWeekExport, handleExportToExcel]);

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    setStartEpoch(null);
    setEndEpoch(null);
    setSelectedTimePeriod(TimePeriod.Today);
    setSelectedRegion([]); // Bölge seçimlerini temizle
    setSelectedSubRegion([]); // Alt bölge seçimlerini temizle
    setSelectedMachine([]); // Otomat seçimlerini de temizle
    // Applied state'leri de temizle
    setAppliedMachine([]);
    setAppliedRegion([]);
    setAppliedSubRegion([]);
    setAppliedMachines([...allMachines]);
    setSelectedPaymentTypes([]);
    setAppliedPaymentTypes([]);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {hasWidgets && (
        <Box>
          <Card
            sx={{
              mt: 2,
              mb: 2,
              borderWidth: 2,
              borderStyle: "solid",
            }}
          >
            <CardContent>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", md: "flex-start" }}
              >
                {/* Başlık */}
                <Box sx={{ width: { xs: "100%", md: "auto" } }}>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.8rem" },
                      mb: { xs: 2, md: 0 },
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("menu.vending-sales", "Otomat Satışları")}
                  </Typography>
                </Box>

                <Box sx={{ flexGrow: 1, display: { xs: "none", md: "block" } }} />

                {/* Filtre kontrolleri */}
                <Stack spacing={1.5} sx={{ width: { xs: "100%", md: "auto" } }}>
                  {/* Satır 1: Hızlı butonlar (orta) + Tarih seçiciler (sağ) */}
                  <Box
                    sx={{
                      display: { xs: "flex", sm: "grid" },
                      flexDirection: "column",
                      gridTemplateColumns: { sm: "1fr auto 1fr" },
                      alignItems: { sm: "center" },
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: { xs: "none", sm: "block" } }} />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                      {[
                        { period: TimePeriod.Today, label: t("widgets.today", "Bugün") },
                        { period: TimePeriod.Yesterday, label: t("widgets.yesterday", "Dün") },
                        { period: TimePeriod.ThisWeek, label: t("widgets.thisWeek", "Bu Hafta") },
                        { period: TimePeriod.LastWeek, label: t("widgets.lastWeek", "Geçen Hafta") },
                        { period: TimePeriod.ThisMonth, label: t("widgets.thisMonth", "Bu Ay") },
                        { period: TimePeriod.LastMonth, label: t("widgets.lastMonth", "Geçen Ay") },
                      ].map(({ period, label }) => (
                        <Button
                          key={period}
                          variant={selectedTimePeriod === period ? "contained" : "outlined"}
                          disabled={dataLoading}
                          onClick={() => handleQuickFilter(period)}
                          size="small"
                          sx={{ borderRadius: 1, textTransform: "none", fontWeight: 500 }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Stack>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ justifyContent: { sm: "flex-end" } }}>
                      <TextField
                        label={t("widgets.startDate", "Başlangıç")}
                        type="datetime-local"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ width: { xs: "100%", sm: 200 }, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                      />
                      <TextField
                        label={t("widgets.endDate", "Bitiş")}
                        type="datetime-local"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ width: { xs: "100%", sm: 200 }, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                      />
                    </Stack>
                  </Box>

                  <Divider sx={{ my: 1 }} />
                  {/* Satır 2: Dropdownlar + Aksiyon butonları */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <TextField
                      select
                      size="small"
                      label={t("vendingSales.viewMode", "Görünüm")}
                      value={groupMode}
                      onChange={(e) => setGroupMode(e.target.value)}
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 160 }}
                    >
                      <option value="default">{t("vendingSales.viewDefault", "Tüm Satışlar")}</option>
                      <option value="product">{t("vendingSales.viewByProduct", "Ürüne Göre")}</option>
                      <option value="day">{t("vendingSales.viewByDay", "Güne Göre")}</option>
                      <option value="nid">{t("vendingSales.viewByMachine", "Otomata Göre")}</option>
                    </TextField>

                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      size="small"
                      options={PAYMENT_TYPES}
                      getOptionLabel={(option) => option.label}
                      value={PAYMENT_TYPES.filter((p) => selectedPaymentTypes.includes(p.value))}
                      onChange={(event, newValue) => {
                        setSelectedPaymentTypes(newValue.map((p) => p.value));
                      }}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.label}
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={option.label} size="small" {...getTagProps({ index })} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label={t("vendingSales.paymentType", "Ödeme Tipi")}
                          placeholder={selectedPaymentTypes.length === 0 ? t("vendingSales.allPayments", "Tümü") : ""}
                        />
                      )}
                      sx={{ minWidth: 180 }}
                    />

                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      limitTags={2}
                      size="small"
                      options={regions.filter((region) =>
                        region.name.toLowerCase().includes(regionSearchText.toLowerCase())
                      )}
                      getOptionLabel={(option) => option.name}
                      value={regions.filter((r) => selectedRegion.includes(r.idRegion))}
                      onChange={(event, newValue) => {
                        setSelectedRegion(newValue.map((r) => r.idRegion));
                      }}
                      popupIcon={<ArrowDropDownIcon sx={{ color: "text.secondary" }} />}
                      slotProps={{
                        clearIndicator: {
                          sx: { "& .MuiSvgIcon-root": { fontSize: "1.2rem" } },
                        },
                      }}
                      ListboxComponent={React.forwardRef((props, ref) => (
                        <Box ref={ref}>
                          <Box
                            sx={{
                              p: 1,
                              position: "sticky",
                              top: 0,
                              bgcolor: "background.paper",
                              zIndex: 1,
                              borderBottom: 1,
                              borderColor: "divider",
                            }}
                          >
                            <TextField
                              size="small"
                              fullWidth
                              placeholder={t("widgets.searchInList", "Listede ara...")}
                              value={regionSearchText}
                              onChange={(e) => setRegionSearchText(e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </Box>
                          <ul {...props} style={{ ...props.style, padding: 0 }} />
                        </Box>
                      ))}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.name}
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            size="small"
                            {...getTagProps({ index })}
                            sx={{
                              maxWidth: 150,
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("widgets.selectRegion", "Bölge Seç")}
                          placeholder={selectedRegion.length === 0 ? t("widgets.searchRegion", "Bölge ara...") : ""}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      onClose={() => setRegionSearchText("")}
                      sx={{
                        minWidth: 200,
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiAutocomplete-endAdornment": {
                          "& .MuiButtonBase-root": { "&:hover": { backgroundColor: "transparent" } },
                        },
                      }}
                      noOptionsText={t("widgets.noRegions", "Bölge bulunamadı")}
                      isOptionEqualToValue={(option, value) => option.idRegion === value.idRegion}
                    />

                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      limitTags={2}
                      size="small"
                      disabled={selectedRegion.length === 0}
                      options={subRegions.filter((subRegion) =>
                        subRegion.name.toLowerCase().includes(subRegionSearchText.toLowerCase())
                      )}
                      getOptionLabel={(option) => option.name}
                      value={subRegions.filter((r) => selectedSubRegion.includes(r.idRegion))}
                      onChange={(event, newValue) => {
                        setSelectedSubRegion(newValue.map((r) => r.idRegion));
                      }}
                      popupIcon={<ArrowDropDownIcon sx={{ color: "text.secondary" }} />}
                      slotProps={{
                        clearIndicator: {
                          sx: { "& .MuiSvgIcon-root": { fontSize: "1.2rem" } },
                        },
                      }}
                      ListboxComponent={React.forwardRef((props, ref) => (
                        <Box ref={ref}>
                          <Box
                            sx={{
                              p: 1,
                              position: "sticky",
                              top: 0,
                              bgcolor: "background.paper",
                              zIndex: 1,
                              borderBottom: 1,
                              borderColor: "divider",
                            }}
                          >
                            <TextField
                              size="small"
                              fullWidth
                              placeholder={t("widgets.searchInList", "Listede ara...")}
                              value={subRegionSearchText}
                              onChange={(e) => setSubRegionSearchText(e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </Box>
                          <ul {...props} style={{ ...props.style, padding: 0 }} />
                        </Box>
                      ))}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.name}
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            size="small"
                            {...getTagProps({ index })}
                            sx={{
                              maxWidth: 150,
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("widgets.selectSubRegion", "Alt Bölge Seç")}
                          placeholder={selectedSubRegion.length === 0 ? t("widgets.searchSubRegion", "Alt bölge ara...") : ""}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      onClose={() => setSubRegionSearchText("")}
                      sx={{
                        minWidth: 200,
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiAutocomplete-endAdornment": {
                          "& .MuiButtonBase-root": { "&:hover": { backgroundColor: "transparent" } },
                        },
                      }}
                      noOptionsText={t("widgets.noSubRegions", "Alt bölge bulunamadı")}
                      isOptionEqualToValue={(option, value) => option.idRegion === value.idRegion}
                    />

                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      limitTags={2}
                      size="small"
                      options={machines.filter((machine) =>
                        machine.name.toLowerCase().includes(machineSearchText.toLowerCase())
                      )}
                      getOptionLabel={(option) => option.name}
                      value={machines.filter((m) => selectedMachine.includes(m.name))}
                      onChange={(event, newValue) => {
                        setSelectedMachine(newValue.map((m) => m.name));
                      }}
                      popupIcon={<ArrowDropDownIcon sx={{ color: "text.secondary" }} />}
                      slotProps={{
                        clearIndicator: {
                          sx: { "& .MuiSvgIcon-root": { fontSize: "1.2rem" } },
                        },
                      }}
                      ListboxComponent={React.forwardRef((props, ref) => (
                        <Box ref={ref}>
                          <Box
                            sx={{
                              p: 1,
                              position: "sticky",
                              top: 0,
                              bgcolor: "background.paper",
                              zIndex: 1,
                              borderBottom: 1,
                              borderColor: "divider",
                            }}
                          >
                            <TextField
                              size="small"
                              fullWidth
                              placeholder={t("widgets.searchInList", "Listede ara...")}
                              value={machineSearchText}
                              onChange={(e) => setMachineSearchText(e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </Box>
                          <ul {...props} style={{ ...props.style, padding: 0 }} />
                        </Box>
                      ))}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.name}
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            size="small"
                            {...getTagProps({ index })}
                            sx={{
                              maxWidth: 150,
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("widgets.selectMachine", "Otomat Seç")}
                          placeholder={selectedMachine.length === 0 ? t("widgets.searchMachine", "Otomat ara...") : ""}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      onClose={() => setMachineSearchText("")}
                      sx={{
                        minWidth: 200,
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiAutocomplete-endAdornment": {
                          "& .MuiButtonBase-root": { "&:hover": { backgroundColor: "transparent" } },
                        },
                      }}
                      noOptionsText={t("widgets.noMachines", "Otomat bulunamadı")}
                      isOptionEqualToValue={(option, value) => option.idNode === value.idNode}
                    />
                    <Button
                      variant="contained"
                      disabled={dataLoading}
                      onClick={handleApplyFilter}
                      size="small"
                      sx={{ borderRadius: 1, textTransform: "none", fontWeight: 500 }}
                    >
                      {dataLoading ? <CircularProgress size={20} /> : t("widgets.apply", "Uygula")}
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={dataLoading}
                      onClick={handleClearFilter}
                      size="small"
                      sx={{ borderRadius: 1, textTransform: "none", fontWeight: 500 }}
                    >
                      {t("widgets.clear", "Temizle")}
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={dataLoading}
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={handleExportToExcel}
                      sx={{ borderRadius: 1, textTransform: "none", fontWeight: 500, color: "white" }}
                    >
                      {t("vendingSales.downloadReport", "Raporu İndir")}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {widgetData.widgets.map((row, rowIndex) => (
            <Grid container spacing={2} key={`row-${rowIndex}`}>
              {row.map((widget, colIndex) => (
                <Grid
                  item
                  xs={12}
                  sm={12 / row.length}
                  key={`widget-${rowIndex}-${colIndex}`}
                >
                  {widget.widgetType === "list" && (
                    <WidgetList
                      widget={widget}
                      objectData={objectData}
                      startEpoch={startEpoch}
                      endEpoch={endEpoch}
                      selectedTimePeriod={selectedTimePeriod}
                      selectedMachine={appliedMachine}
                      selectedRegion={appliedRegion}
                      selectedSubRegion={appliedSubRegion}
                      allRegions={allRegions}
                      getAllChildRegions={getAllChildRegions}
                      machines={appliedMachines}
                      onExportToExcel={handleSetExportFunction}
                      onExportTableToExcel={handleSetTableExportFunction}
                      onLoadingChange={setDataLoading}
                      filterStartDate={startDate}
                      filterEndDate={endDate}
                      filterTimePeriod={selectedTimePeriod}
                      filterSelectedMachine={selectedMachine}
                      filterMachines={machines}
                      filterSelectedRegion={selectedRegion}
                      filterSelectedSubRegion={selectedSubRegion}
                      groupMode={groupMode}
                      selectedPaymentTypes={appliedPaymentTypes}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          ))}
        </Box>
      )}
    </>
  );
}
